import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import useSWR from 'swr';
import {
  searchQueryAtom,
  allSearchResultsAtom,
  SearchQuery,
} from 'packages/omnitag/state/text-search';
import moment from 'moment';
import {
  omnitagSearchResponseZod,
  omnitagSearchSizeResponseZod,
} from 'packages/lib/api/omnitag/contract/omnitag-search';
import { searchResultSizeRespAtom } from 'packages/omnitag/state/text-search';
import { OmnitagFilter } from 'packages/lib/api/omnitag/contract/omnitag-filters';
import { useGetFiltersListAPI } from 'packages/omnitag/api/use-get-filters-api';
import { omnitagAPIPost } from 'packages/lib/api/omnitag/rest';

// Aug-11-2025 - Used Gemini 2.5 Agent to make filters dynamic (affected getOTFilterQueryParams)
// Aug-20-2025 - Used Gemini 2.5 Agent to add support for 'number' filters
// Sep-15-2025 - Used Gemini 2.5 Agent to convert from GET to POST

/**
 * Uses params from URL to add appropriate filters.
 */
export const useDoSWRSearchQueriesBasedOnURL = (): void => {
  const filtersSWR = useGetFiltersListAPI();
  const searchQuery = useAtomValue(searchQueryAtom);
  const setResults = useSetAtom(allSearchResultsAtom);
  const setResultSizeResp = useSetAtom(searchResultSizeRespAtom);

  const searchIsActive = searchQuery && searchQuery.text.length > 0;
  const hasPagination =
    searchQuery && searchQuery.pageNo && searchQuery.pageSize;
  const dynamicFilters = filtersSWR.data?.filters ?? [];

  const swrResult = useSWR(
    searchIsActive && hasPagination
      ? [
          '/vision/search',
          getOTSearchPOSTBody(searchQuery, dynamicFilters, true),
        ]
      : null,
    ([url, body]) => omnitagAPIPost(url, body, omnitagSearchResponseZod.parse),
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  useEffect(() => {
    setResults(swrResult);
  }, [setResults, swrResult]);

  const searchSizeResp = useSWR(
    searchIsActive
      ? [
          '/vision/search/count',
          getOTSearchPOSTBody(searchQuery, dynamicFilters, false),
        ]
      : null,
    ([url, body]) =>
      omnitagAPIPost(url, body, omnitagSearchSizeResponseZod.parse),
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  useEffect(() => {
    setResultSizeResp(searchSizeResp);
  }, [setResultSizeResp, searchSizeResp]);
};

/** Get POST body for both /search (pagination included) and /search/count (pagination not included) endpoints */
function getOTSearchPOSTBody(
  query: SearchQuery,
  dynamicFilters: OmnitagFilter[],
  includePagination: boolean
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    tagName: query.text,
  };

  if (query.logNames && query.logNames.length > 0) {
    body['logNames'] = query.logNames;
  }

  for (const filter of dynamicFilters) {
    if (filter.filterType === 'dateRange') {
      const from = query[`${filter.paramName}From`];
      const to = query[`${filter.paramName}To`];
      if (from) {
        body[`${filter.paramName}From`] = moment(from)
          .utcOffset(0)
          .format('YYYY-MM-DD');
      }
      if (to) {
        body[`${filter.paramName}To`] = moment(to)
          .utcOffset(0)
          .format('YYYY-MM-DD');
      }
    } else {
      const value = query[filter.paramName];
      const valueStr = value as string;
      if (value !== undefined && value !== null) {
        if (filter.filterType === 'select' && Array.isArray(value)) {
          if ((value as string[]).length > 0) {
            body[filter.paramName] = value;
          }
        } else if (
          filter.filterType === 'text' &&
          typeof value === 'string' &&
          valueStr.length > 0
        ) {
          body[filter.paramName] = valueStr.split(',');
        } else if (
          filter.filterType === 'number' &&
          typeof value === 'number'
        ) {
          body[filter.paramName] = value;
        } else if (
          filter.filterType === 'toggle' &&
          typeof value === 'boolean'
        ) {
          body[filter.paramName] = value;
        }
      }
    }
  }

  if (includePagination && query.pageNo) {
    body['pageNo'] = query.pageNo;
  }
  if (includePagination && query.pageSize) {
    body['pageSize'] = query.pageSize;
  }

  return body;
}
